import { useMutation, useQueryClient } from '@tanstack/react-query';

import createCrudService from '@src/core/api/create-crud-service';
import { request } from '@core/api/request';
import { QUERY_KEYS } from '@src/shared/constants/query-keys';
import type { TBaseQuery } from '@src/shared/types/base-query';
import { showToast } from '@src/shared/utils/helpers/show-toast';

/** The base a content row belongs to, as the read serializers nest it. */
export type KnowledgeBaseRef = { id: number; name: string; is_active: boolean };

type ContentQuery = TBaseQuery & {
  kbase: number;
  q?: string;
  is_active?: boolean;
};

const success = (title: string) => () =>
  showToast({ title, variant: 'success' });

/** Every content list refetches the base too: its tab counts come from the
 * base row, and adding or deleting a document changes them. */
const REVALIDATE_BASE = [QUERY_KEYS.getKnowledgeBases];

// ---------------------------------------------------------------- documents

export type KnowledgeDocumentStatus =
  | 'PENDING'
  | 'PROCESSING'
  | 'READY'
  | 'PARTIAL'
  | 'FAILED';

export type KnowledgeDocument = {
  id: number;
  kbase: number;
  source: string;
  title: string | null;
  file_format: string;
  status: KnowledgeDocumentStatus;
  error_message: string;
  chunk_count: number;
  metadata?: {
    total_pages?: number;
    indexed_pages?: number;
    notes?: string[];
    reindex_required?: boolean;
  };
  created_at: string;
  updated_at: string;
};

export type KnowledgeDocumentQuery = ContentQuery & {
  status?: KnowledgeDocumentStatus[];
};

/** Formats the ingestion service accepts (its `SUPPORTED_EXTENSIONS`). */
export const DOCUMENT_EXTENSIONS = [
  '.pdf',
  '.docx',
  '.md',
  '.txt',
  '.html',
  '.htm',
];
export const DOCUMENT_MAX_MB = 20;

export const knowledgeDocumentsApi = {
  ...createCrudService<KnowledgeDocument, KnowledgeDocumentQuery>({
    queryKey: [QUERY_KEYS.getKnowledgeDocuments],
    url: 'knowledge-documents',
    validateQuery: REVALIDATE_BASE,
    factory: {
      delete: { tanstackOptions: { onSuccess: success('File deleted') } },
      infiniteQuery: {
        tanstackOptions: {
          // Indexing runs on a worker with nothing to push the result back, so
          // the list polls while any row is mid-flight and settles on its own.
          refetchInterval: (query) =>
            (query.state.data?.pages ?? []).some((page) =>
              page.results.some(
                (row) => row.status === 'PENDING' || row.status === 'PROCESSING'
              )
            )
              ? 3000
              : false,
        },
      },
    },
  }),

  /** Upload one file into a base. The document lands at PENDING (202) and the
   * list's polling reports when it is searchable. */
  useUpload: () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: ({ kbase, file }: { kbase: number; file: File }) => {
        const form = new FormData();
        form.append('file', file);
        form.append('kbase', String(kbase));
        return request<KnowledgeDocument>({
          url: 'knowledge-documents/upload/',
          method: 'POST',
          body: form,
        });
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.getKnowledgeDocuments],
        });
        queryClient.invalidateQueries({ queryKey: REVALIDATE_BASE });
      },
    });
  },

  useReindex: () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (id: number) =>
        request<KnowledgeDocument>({
          url: `knowledge-documents/${id}/reindex/`,
          method: 'POST',
        }),
      onSuccess: success('Reindexing queued'),
      onSettled: () =>
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.getKnowledgeDocuments],
        }),
    });
  },
};

// --------------------------------------------------------------------- faqs

export type KnowledgeFaq = {
  id: number;
  kbase: KnowledgeBaseRef;
  question: string;
  answer: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type KnowledgeFaqPayload = {
  kbase: number;
  question: string;
  answer: string;
  is_active?: boolean;
};

export const FAQ_MAX_LENGTH = 1000;

export const knowledgeFaqsApi = createCrudService<
  KnowledgeFaq,
  ContentQuery,
  KnowledgeFaqPayload
>({
  queryKey: [QUERY_KEYS.getKnowledgeFaqs],
  url: 'knowledge-faqs',
  validateQuery: REVALIDATE_BASE,
  factory: {
    create: { tanstackOptions: { onSuccess: success('FAQ added') } },
    update: { tanstackOptions: { onSuccess: success('FAQ updated') } },
    delete: { tanstackOptions: { onSuccess: success('FAQ deleted') } },
  },
});

export const importKnowledgeFaqs = createCrudService<
  { detail: string },
  Record<string, never>,
  { file: File; kbase: number }
>({
  queryKey: [QUERY_KEYS.getKnowledgeFaqs, 'import'],
  url: 'knowledge-faqs/import-faqs',
  validateQuery: REVALIDATE_BASE,
  factory: {
    create: { tanstackOptions: { onSuccess: success('FAQs imported') } },
  },
});

// --------------------------------------------------------------- rich texts

export type KnowledgeRichText = {
  id: number;
  kbase: KnowledgeBaseRef;
  name: string;
  /** Editor HTML, stored as sent. Render it through `SafeHtml` only. */
  content: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type KnowledgeRichTextPayload = {
  kbase: number;
  name: string;
  content: string;
  is_active?: boolean;
};

export const knowledgeRichTextsApi = createCrudService<
  KnowledgeRichText,
  ContentQuery,
  KnowledgeRichTextPayload
>({
  queryKey: [QUERY_KEYS.getKnowledgeRichTexts],
  url: 'knowledge-rich-texts',
  validateQuery: REVALIDATE_BASE,
  factory: {
    create: { tanstackOptions: { onSuccess: success('Text added') } },
    update: { tanstackOptions: { onSuccess: success('Text updated') } },
    delete: { tanstackOptions: { onSuccess: success('Text deleted') } },
  },
});

// ----------------------------------------------------------------- websites

export type KnowledgeWebsiteMode = 'EXACT' | 'PATH' | 'DOMAIN';
export type KnowledgeWebsiteStatus =
  | 'PENDING'
  | 'CRAWLING'
  | 'READY'
  | 'PARTIAL'
  | 'FAILED';

export type KnowledgeWebsite = {
  id: number;
  kbase: KnowledgeBaseRef;
  url: string;
  mode: KnowledgeWebsiteMode;
  status: KnowledgeWebsiteStatus;
  error_message: string | null;
  last_crawled_at: string | null;
  page_count: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type KnowledgeWebsitePayload = {
  kbase: number;
  url: string;
  mode: KnowledgeWebsiteMode;
  is_active?: boolean;
};

export type KnowledgeWebsiteQuery = ContentQuery & {
  mode?: KnowledgeWebsiteMode[];
  status?: KnowledgeWebsiteStatus[];
};

export const WEBSITE_MODES: { value: KnowledgeWebsiteMode; label: string }[] = [
  { value: 'DOMAIN', label: 'Extract URLs in this domain' },
  { value: 'PATH', label: 'Extract URLs under this path' },
  { value: 'EXACT', label: 'This exact URL only' },
];

export const knowledgeWebsitesApi = createCrudService<
  KnowledgeWebsite,
  KnowledgeWebsiteQuery,
  KnowledgeWebsitePayload
>({
  queryKey: [QUERY_KEYS.getKnowledgeWebsites],
  url: 'knowledge-websites',
  validateQuery: REVALIDATE_BASE,
  factory: {
    create: { tanstackOptions: { onSuccess: success('URL added') } },
    update: { tanstackOptions: { onSuccess: success('URL updated') } },
    delete: { tanstackOptions: { onSuccess: success('URL deleted') } },
  },
});
